/**
 * Shared helpers for document view/edit modals - company profile, number-to-words, logo URL.
 * Used by Quotes, Invoices, BillsOfSupply, CreditNotes, Receipts, DeliveryChallans.
 */

import { API_BASE_URL } from '../../../../utils/config';
import {
  extractCustomerDetails,
  extractClientDetails,
  formatAddress,
  getContactId,
} from './contactHelpers';

export const getCompanyLogoUrl = (profileData) => {
  if (!profileData) return null;
  const rawLogo = profileData.company_logo || profileData.logo;
  if (!rawLogo) return null;
  const logoUrl = String(rawLogo);
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) return logoUrl;
  const baseUrl = (API_BASE_URL || '').replace('/api', '');
  return logoUrl.startsWith('/') ? `${baseUrl}${logoUrl}` : `${baseUrl}/${logoUrl}`;
};

export const numberToWords = (num) => {
  if (!num || num === 0) return 'Zero';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convertHundreds = (n) => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertHundreds(n % 100) : '');
  };
  const convert = (n) => {
    if (n === 0) return '';
    if (n < 1000) return convertHundreds(n);
    if (n < 100000) {
      const thousands = Math.floor(n / 1000);
      const remainder = n % 1000;
      return convertHundreds(thousands) + ' Thousand' + (remainder ? ' ' + convertHundreds(remainder) : '');
    }
    if (n < 10000000) {
      const lakhs = Math.floor(n / 100000);
      const remainder = n % 100000;
      return convert(lakhs) + ' Lakh' + (remainder ? ' ' + convert(remainder) : '');
    }
    const crores = Math.floor(n / 10000000);
    const remainder = n % 10000000;
    return convert(crores) + ' Crore' + (remainder ? ' ' + convert(remainder) : '');
  };
  const integerPart = Math.floor(num);
  return convert(integerPart) || 'Zero';
};

/** Line items from invoice / estimate / quote detail (API shape varies). Prefer first non-empty array so empty `items` does not hide `estimate_items`. */
export const getInvoiceLineItems = (invoice) => {
  if (!invoice) return [];
  const candidates = [
    invoice.items,
    invoice.challan_items,
    invoice.challan_line_items,
    invoice.delivery_challan_items,
    invoice.delivery_challan_lines,
    invoice.estimate_items,
    invoice.quote_items,
    invoice.line_items,
    invoice.invoice_items,
    invoice.invoice_lines,
    invoice.invoice_line_items,
    invoice.lines,
    invoice.invoiceitem_set,
    invoice.invoice_item_set,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    if (Array.isArray(c) && c.length > 0) return c;
    if (c && Array.isArray(c.results) && c.results.length > 0) return c.results;
  }
  return [];
};

const toNum = (v, fallback = NaN) => {
  if (v == null || v === '') return fallback;
  const n = typeof v === 'string' ? parseFloat(String(v).replace(/,/g, '')) : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** First strictly positive numeric hit (list/detail serializers use many names). */
const firstPositiveAmount = (obj, keys) => {
  if (!obj) return NaN;
  for (const k of keys) {
    const n = toNum(obj[k], NaN);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return NaN;
};

const NESTED_TOTAL_OBJECTS = ['summary', 'totals', 'amounts', 'financial', 'invoice_summary', 'pricing'];

const DOCUMENT_SUBTOTAL_KEYS = [
  'subtotal',
  'sub_total',
  'total_taxable_value',
  'taxable_total',
  'total_taxable',
  'taxable_amount',
  'base_total',
  'total_excluding_tax',
  'amount_excluding_tax',
  'subTotal',
  'totalTaxableValue',
];

const DOCUMENT_TOTAL_KEYS = [
  'total_amount',
  'total_invoice_value',
  'total',
  'grand_total',
  'amount',
  'total_value',
  'net_total',
  'final_total',
  'payable_amount',
  'invoice_total',
  'challan_total',
  'document_total',
  'gross_total',
  'grandTotal',
  'totalAmount',
  'totalInvoiceValue',
  'line_total_sum',
  'total_line_amount',
  'bill_amount',
  'order_total',
  'value',
];

const DOCUMENT_TAX_KEYS = [
  'tax_amount',
  'total_tax',
  'tax_total',
  'totalTax',
  'taxAmount',
];

/**
 * Line total for table display when API uses `line_total`, `taxable_value`, etc. instead of `amount`.
 */
export const getLineItemLineTotal = (item) => {
  if (!item) return 0;
  const lineTotal = toNum(
    item.amount ?? item.line_total ?? item.line_amount ?? item.taxable_value ?? item.taxable_amount ?? item.subtotal,
    NaN
  );
  if (Number.isFinite(lineTotal)) return lineTotal;
  const isService =
    item.item_type === 'service' ||
    String(item.item_type || '').toLowerCase() === 'service' ||
    (!!item.sac_code && !item.hsn_code);
  const qtyRaw = toNum(item.quantity, NaN);
  const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
  const rate = toNum(item.rate ?? item.unit_price ?? item.price ?? item.unit_rate, NaN);
  if (Number.isFinite(rate)) return rate * (isService ? 1 : quantity);
  return 0;
};

/** Sum of line taxable amounts (when API omits document-level subtotal). */
const sumLineItemsTaxable = (doc) => {
  const items = getInvoiceLineItems(doc);
  return items.reduce((s, it) => s + getLineItemLineTotal(it), 0);
};

/** Sum of per-line tax (when API omits document-level tax_amount). */
const sumLineItemsTax = (doc) => {
  const items = getInvoiceLineItems(doc);
  return items.reduce((s, it) => s + toNum(it.tax_amount, 0), 0);
};

/**
 * Document-level subtotal / taxable (serializer field names vary).
 * Falls back to summing lines when header fields are missing (e.g. some delivery challan detail responses).
 */
export const getDocumentSubtotal = (doc) => {
  if (!doc) return 0;
  let v = firstPositiveAmount(doc, DOCUMENT_SUBTOTAL_KEYS);
  if (!Number.isFinite(v)) {
    v = toNum(
      doc.subtotal ?? doc.total_taxable_value ?? doc.taxable_total ?? doc.total_taxable,
      NaN
    );
  }
  if (Number.isFinite(v) && v > 0) return v;
  const fromLines = sumLineItemsTaxable(doc);
  if (fromLines > 0) return fromLines;
  for (const nk of NESTED_TOTAL_OBJECTS) {
    const nested = doc[nk];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const inner = firstPositiveAmount(nested, DOCUMENT_SUBTOTAL_KEYS);
      if (Number.isFinite(inner) && inner > 0) return inner;
    }
  }
  return Number.isFinite(v) && v >= 0 ? v : 0;
};

/**
 * Document-level grand total.
 * Falls back to line sums, header subtotal + tax, and nested summary objects (common on list serializers).
 */
export const getDocumentTotalAmount = (doc, depth = 0) => {
  if (!doc || typeof doc !== 'object' || depth > 3) return 0;
  let v = firstPositiveAmount(doc, DOCUMENT_TOTAL_KEYS);
  if (!Number.isFinite(v)) {
    v = toNum(
      doc.total_amount ??
        doc.total_invoice_value ??
        doc.total ??
        doc.grand_total ??
        doc.amount ??
        doc.total_value,
      NaN
    );
  }
  if (Number.isFinite(v) && v > 0) return v;
  for (const nk of NESTED_TOTAL_OBJECTS) {
    const nested = doc[nk];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const t = getDocumentTotalAmount(nested, depth + 1);
      if (t > 0) return t;
    }
  }
  const sub = sumLineItemsTaxable(doc);
  const tax = sumLineItemsTax(doc);
  const derivedFromLines = sub + tax;
  if (derivedFromLines > 0) return derivedFromLines;
  const subHeader = getDocumentSubtotal(doc);
  const taxHeader = getDocumentTaxAmount(doc);
  const derivedFromHeader = subHeader + taxHeader;
  if (derivedFromHeader > 0) return derivedFromHeader;
  return Number.isFinite(v) ? v : 0;
};

export const getDocumentTaxAmount = (doc) => {
  if (!doc) return 0;
  let v = firstPositiveAmount(doc, DOCUMENT_TAX_KEYS);
  if (!Number.isFinite(v)) {
    v = toNum(doc.tax_amount ?? doc.total_tax ?? doc.tax_total, NaN);
  }
  if (Number.isFinite(v) && v > 0) return v;
  const fromLines = sumLineItemsTax(doc);
  if (fromLines > 0) return fromLines;
  for (const nk of NESTED_TOTAL_OBJECTS) {
    const nested = doc[nk];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const inner = firstPositiveAmount(nested, DOCUMENT_TAX_KEYS);
      if (Number.isFinite(inner) && inner > 0) return inner;
    }
  }
  return Number.isFinite(v) ? v : 0;
};

/**
 * Bill To for display: nested customer/client, fetched contact, or flat serializer fields.
 */
export const getBillToFromInvoice = (invoice, resolvedCustomer = null, resolvedClient = null) => {
  if (!invoice) {
    return { name: '', company: '', address: '', gstin: '', pan: '' };
  }
  if (resolvedCustomer) {
    return extractCustomerDetails(resolvedCustomer);
  }
  if (invoice.customer && typeof invoice.customer === 'object') {
    return extractCustomerDetails(invoice.customer);
  }
  if (resolvedClient) {
    return extractClientDetails(resolvedClient);
  }
  if (invoice.client && typeof invoice.client === 'object') {
    return extractClientDetails(invoice.client);
  }
  return {
    name:
      invoice.customer_name ||
      invoice.client_name ||
      invoice.billing_name ||
      invoice.bill_to_name ||
      '',
    company:
      invoice.customer_company ||
      invoice.client_company ||
      invoice.billing_company ||
      '',
    address: formatAddress(
      invoice.customer_address,
      invoice.client_address,
      invoice.billing_address,
      invoice.billing_address_line1,
      invoice.billing_address_line2,
      invoice.billing_city,
      invoice.billing_state,
      invoice.billing_postal_code,
      invoice.billing_pincode,
      invoice.shipping_address
    ),
    gstin:
      invoice.customer_gstin ||
      invoice.client_gstin ||
      invoice.billing_gstin ||
      invoice.gstin ||
      '',
    pan:
      invoice.customer_pan ||
      invoice.client_pan ||
      invoice.billing_pan ||
      invoice.pan ||
      '',
  };
};

/**
 * Bill To for receipts when the API returns only customer/client IDs (no nested object or flat name fields).
 * Prefers receipt-owned contact data; uses linked invoice only as a last resort.
 */
export const resolveBillToForReceipt = (receipt, customersList = [], clientsList = [], invoiceDetail = null) => {
  if (!receipt) {
    return { name: '', company: '', address: '', gstin: '', pan: '' };
  }
  const hasFlat =
    (receipt.customer_name && String(receipt.customer_name).trim()) ||
    (receipt.client_name && String(receipt.client_name).trim()) ||
    (receipt.customer_company && String(receipt.customer_company).trim()) ||
    (receipt.client_company && String(receipt.client_company).trim()) ||
    (receipt.customer_address && String(receipt.customer_address).trim()) ||
    (receipt.client_address && String(receipt.client_address).trim());
  if (hasFlat) {
    return {
      name: receipt.customer_name || receipt.client_name || '',
      company: receipt.customer_company || receipt.client_company || '',
      address: receipt.customer_address || receipt.client_address || '',
      gstin: receipt.customer_gstin || receipt.client_gstin || '',
      pan: receipt.customer_pan || receipt.client_pan || '',
    };
  }
  if (receipt.customer && typeof receipt.customer === 'object') {
    return extractCustomerDetails(receipt.customer);
  }
  if (receipt.client && typeof receipt.client === 'object') {
    return extractClientDetails(receipt.client);
  }
  const hasCustomerId = receipt.customer != null && receipt.customer !== '';
  const hasClientId = receipt.client != null && receipt.client !== '';
  const contactType = hasCustomerId ? 'customer' : hasClientId ? 'client' : '';
  const contactId = getContactId(receipt.customer) || getContactId(receipt.client);
  if (contactId && contactType === 'customer') {
    const c = customersList.find((x) => String(x.id) === contactId);
    if (c) return extractCustomerDetails(c);
  }
  if (contactId && contactType === 'client') {
    const c = clientsList.find((x) => String(x.id) === contactId);
    if (c) return extractClientDetails(c);
  }
  if (invoiceDetail) {
    return getBillToFromInvoice(invoiceDetail);
  }
  return { name: '', company: '', address: '', gstin: '', pan: '' };
};

/**
 * Line items for receipt view/edit: prefer linked invoice rows; otherwise one row from receipt amount / notes.
 */
export const getReceiptDisplayLineItems = (receipt, invoiceDetail = null) => {
  const fromInvoice = invoiceDetail ? getInvoiceLineItems(invoiceDetail) : [];
  if (fromInvoice.length > 0) return fromInvoice;
  if (!receipt) return [];
  const raw = receipt.amount;
  const amt =
    typeof raw === 'string' ? parseFloat(String(raw).replace(/,/g, '')) : Number(raw);
  if (Number.isFinite(amt) && amt !== 0) {
    const note = receipt.notes != null && String(receipt.notes).trim() ? String(receipt.notes).trim().slice(0, 120) : '';
    return [
      {
        description: note || 'Amount received',
        quantity: 1,
        amount: amt,
        item_type: 'service',
        sac_code: '',
        hsn_code: '',
      },
    ];
  }
  return [];
};

/** Map API invoice line to editable row (InvoiceModal / TaxInvoiceForm-style). */
export const mapApiLineItemToRow = (item, idx) => {
  const desc =
    item.description ??
    item.name ??
    item.title ??
    item.product_name ??
    item.service_name ??
    (item.product && (item.product.name || item.product.service_name || item.product.goods_name)) ??
    '';
  const qtyRaw = toNum(item.quantity, NaN);
  const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
  const rateFromApi = toNum(item.rate ?? item.unit_price ?? item.price ?? item.unit_rate, NaN);
  const lineAmount = toNum(
    item.amount ?? item.line_total ?? item.line_amount ?? item.taxable_value ?? item.taxable_amount ?? item.subtotal,
    NaN
  );
  const rate =
    Number.isFinite(rateFromApi) && rateFromApi >= 0
      ? rateFromApi
      : Number.isFinite(lineAmount) && quantity > 0
        ? lineAmount / quantity
        : Number.isFinite(lineAmount)
          ? lineAmount
          : 0;
  const isService =
    item.item_type === 'service' ||
    String(item.item_type || '').toLowerCase() === 'service' ||
    (!!item.sac_code && !item.hsn_code);
  return {
    id: item.id ?? `row-${idx}-${Date.now()}`,
    sac_code: item.sac_code || '',
    hsn_code: item.hsn_code || '',
    item_type: isService ? 'service' : 'goods',
    description: desc,
    quantity: isService ? 1 : quantity,
    amount: rate,
    tax_rate: item.tax_rate != null ? toNum(item.tax_rate, 0) : 0,
    tax_amount: item.tax_amount != null ? toNum(item.tax_amount, 0) : 0,
  };
};

/** Grand total: tax adds to subtotal; discount subtracts, surcharge adds adjustment. */
export const computeDocumentTotal = (subtotal, taxAmount, adjustmentAmount, adjustmentType = 'discount') => {
  const parseMoney = (v) => {
    if (v == null || v === '') return 0;
    const n = typeof v === 'string' ? parseFloat(String(v).replace(/,/g, '')) : Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const base = parseMoney(subtotal) + parseMoney(taxAmount);
  const adj = parseMoney(adjustmentAmount);
  return adjustmentType === 'surcharge' ? base + adj : base - adj;
};

export const defaultInvoiceLineRow = (id) => ({
  id,
  sac_code: '',
  hsn_code: '',
  item_type: 'goods',
  description: '',
  quantity: 1,
  amount: 0,
  tax_rate: 0,
  tax_amount: 0,
});

export const parseCompanyProfile = (profiles) => {
  const arr = Array.isArray(profiles) ? profiles : (profiles?.results || []);
  if (!arr.length) return null;
  const p = arr[0];
  return {
    name: p.company_name || p.companyName || '',
    address: p.address || '',
    gstin: p.gstin || p.gst_in || '',
    phone: p.phone_number || p.phone || '',
    email: p.email || '',
    accountName: p.account_name || p.accountName || '',
    bankName: p.bank_name || p.bankName || '',
    accountNo: p.account_no || p.accountNo || p.account_number || '',
    ifscCode: p.ifsc_code || p.ifscCode || '',
    branch: p.branch || '',
    logoUrl: getCompanyLogoUrl(p)
  };
};
